export type HelpCategory = 'pages' | 'contestOverview' | 'scoring' | 'roles';

export interface HelpIndexEntry {
  key: string;
  labelKey: string;
  /** Only set for entries that share a base label but differ by a qualifier (e.g. role). */
  qualifierKey?: string;
  category: HelpCategory;
  /**
   * Plain (untranslated) search terms for entries whose title alone wouldn't surface
   * them — e.g. the role docs are just labeled "Organizer"/"Judge"/"Dancer" (that label
   * is reused as a chip elsewhere, so it can't be padded), but their content is about
   * scoring, peer voting, managing rounds. Keep this short; most entries don't need it.
   */
  keywords?: string[];
}

// Reuses the same i18n keys already displayed elsewhere in the app (route breadcrumbs,
// nounForms, contestRole, scoring.types) so labels never drift from their source of truth.
export const HELP_INDEX: HelpIndexEntry[] = [
  { key: 'settings', labelKey: 'settings.pageTitle', category: 'pages' },
  { key: 'account', labelKey: 'wrapper.accountAction', category: 'pages' },
  { key: 'contest-list', labelKey: 'nounForms.contest.plural', category: 'pages' },
  { key: 'participant-list', labelKey: 'quickActions.myContests', category: 'pages' },
  { key: 'contest-dashboard', labelKey: 'nounForms.dashboard.singular', category: 'pages' },
  { key: 'contest-setup', labelKey: 'breadcrumb.setup', category: 'pages' },
  { key: 'leaderboard', labelKey: 'labels.leaderboard', category: 'pages' },
  { key: 'admin-dashboard', labelKey: 'adminDashboard.title', category: 'pages' },
  { key: 'mailer-template-preview', labelKey: 'mailerTemplatePreview.title', category: 'pages' },
  { key: 'user-list', labelKey: 'nounForms.user.plural', category: 'pages' },
  { key: 'organizer-list', labelKey: 'nounForms.organizer.plural', category: 'pages' },
  { key: 'dancer-list', labelKey: 'nounForms.dancer.plural', category: 'pages' },
  { key: 'judge-list', labelKey: 'nounForms.judge.plural', category: 'pages' },
  { key: 'pool-list', labelKey: 'nounForms.pool.plural', category: 'pages' },
  { key: 'score-list', labelKey: 'nounForms.score.plural', category: 'pages' },
  { key: 'round-list', labelKey: 'nounForms.round.plural', category: 'pages' },

  {
    key: 'contest-overview-organizer',
    labelKey: 'dashboard.runContest',
    qualifierKey: 'contestRole.organizer',
    category: 'contestOverview',
  },
  {
    key: 'contest-overview-judge',
    labelKey: 'dashboard.runContest',
    qualifierKey: 'contestRole.judge',
    category: 'contestOverview',
  },
  {
    key: 'contest-overview-dancer',
    labelKey: 'dashboard.runContest',
    qualifierKey: 'contestRole.dancer',
    category: 'contestOverview',
  },

  {
    key: 'score-pool-criteria-v1',
    labelKey: 'scoring.types.CRITERIA_V1',
    category: 'scoring',
    keywords: ['points', 'input'],
  },
  {
    key: 'score-pool-ranking-v1',
    labelKey: 'scoring.types.RANKING_V1',
    category: 'scoring',
    keywords: ['drag', 'reorder', 'points'],
  },
  {
    key: 'score-pool-choice-v1',
    labelKey: 'scoring.types.CHOICE_V1',
    category: 'scoring',
    keywords: ['checkbox', 'select', 'advance'],
  },

  {
    key: 'role-organizer',
    labelKey: 'contestRole.organizer',
    category: 'roles',
    keywords: ['manage', 'setup', 'dashboard', 'rounds', 'pools'],
  },
  {
    key: 'role-judge',
    labelKey: 'contestRole.judge',
    category: 'roles',
    keywords: ['score', 'scoring', 'evaluate'],
  },
  {
    key: 'role-dancer',
    labelKey: 'contestRole.dancer',
    category: 'roles',
    keywords: ['vote', 'voting', 'peer', 'score'],
  },
];
